import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, History, Loader2, RefreshCw } from "lucide-react";
import { API, fmtDate, SectionLabel } from "./reviewMeta";

/**
 * Previous reviews (12 month retention). Filterable by proposal
 * number; opening one loads it read only into the page.
 */
export default function ReviewHistory({ onOpen, refreshKey, entryFilter }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(entryFilter || "");

  const load = async (entry) => {
    setLoading(true);
    try {
      const params = {};
      const n = parseInt(entry, 10);
      if (Number.isFinite(n) && n > 0) params.entry_number = n;
      const { data } = await axios.get(`${API}/api/proposals/contract-review/history`, { params });
      setRows(data.reviews || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="contract-review-history">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <SectionLabel>Kept for 12 months</SectionLabel>
          <h2 className="text-xl text-slate-900 flex items-center gap-2">
            <History className="w-5 h-5 text-[#1B4F8A]" /> Previous reviews
          </h2>
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            load(filter);
          }}
        >
          <input
            type="number"
            min="1"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Proposal no."
            data-testid="contract-review-history-filter"
            className="w-32 bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm font-mono text-slate-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC]"
          />
          <button type="submit" data-testid="contract-review-history-refresh" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.1em] text-stone-500 hover:text-slate-900 border border-stone-300 rounded-md px-3 py-2">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Filter
          </button>
        </form>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-stone-500 italic" data-testid="contract-review-history-empty">
          {loading ? "Loading…" : "No completed reviews yet."}
        </p>
      ) : (
        <ul className="divide-y divide-stone-100" data-testid="contract-review-history-list">
          {rows.map((r) => (
            <li key={r.job_id} data-testid={`contract-review-history-item-${r.job_id}`} className="py-3 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm text-slate-900">
                  <span className="font-mono text-[#1B4F8A] mr-2">#{r.entry_number}</span>
                  {r.project_reference || "Untitled proposal"}
                  {r.client_company ? <span className="text-stone-500"> · {r.client_company}</span> : null}
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  {r.filename_in} · {fmtDate(r.created_at)} · {r.created_by || "staff"}
                  {r.email_count ? ` · ${r.email_count} email source(s)` : " · no correspondence"}
                  {r.previous_review_id ? " · revision of an earlier draft" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-rose-600">{r.summary?.red || 0} red</span>
                <span className="text-amber-600">{r.summary?.amber || 0} amber</span>
                <span className="text-emerald-600">{r.summary?.green || 0} note</span>
                {r.decisions_total ? (
                  <span className="text-stone-500">· {r.decisions_included}/{r.decisions_total} kept</span>
                ) : (
                  <span className="text-stone-400">· not built</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => onOpen(r.job_id)}
                data-testid={`contract-review-history-open-${r.job_id}`}
                className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.1em] text-[#1B4F8A] hover:underline"
              >
                Open <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
